import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Shield, Building2, FileText, Loader2, CheckCircle, ChevronRight, Search } from 'lucide-react';
import { getAllClients, type ClientItem } from '../services/clientService';
import {
  getServo2ClientNames,
  getFormulairesByClientName,
  getFormulaireById,
  getFormId,
  filterFormsByClientName,
  sortFormulairesByDateDesc,
  type IncidentFormListItem,
  type IncidentFormData,
} from '../services/rapportsCyberService';
import { generateRapportIncidentFromServo2 } from '../services/rapportsCyberPrismeService';
import { useNavigate } from 'react-router-dom';

interface GestionRapportsCyberModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Libellé affiché dans la liste : priorité au nom du formulaire (comme dans Servo2), pas à la date. */
function getFormLabel(form: IncidentFormListItem): string {
  const name = form.nom ?? form.name ?? form.title;
  if (name !== undefined && name !== null && String(name).trim() !== '') return String(name).trim();
  if (form.incident_number) return String(form.incident_number);
  if (form.date ?? form.début) return String(form.date ?? form.début);
  if (form.created_at) return String(form.created_at);
  return `Formulaire ${getFormId(form)}`;
}

function getFormDate(form: IncidentFormListItem): string | null {
  const d = form.début ?? form.date ?? form.created_at;
  return d != null && String(d).trim() !== '' ? String(d).trim() : null;
}

/** Entrée affichable pour la section "Informations du formulaire" */
interface FormDisplayEntry {
  label: string;
  value: string;
}

/**
 * Normalise les données du formulaire API pour l'affichage : gère les clés numériques (0, 1, 2),
 * les tableaux, et les structures avec champs nommés pour refléter correctement Servo2.
 */
function getFormDisplayEntries(formData: IncidentFormData | null): FormDisplayEntry[] {
  if (!formData || typeof formData !== 'object') return [];

  const entries: FormDisplayEntry[] = [];
  const skipKeys = /^(_|id$|internal)/i;

  // Structure type Servo2 : { fields: [{ label, value }, ...] } ou { reponses: [...] }
  const fields = (formData as Record<string, unknown>).fields ?? (formData as Record<string, unknown>).reponses ?? (formData as Record<string, unknown>).champs;
  if (Array.isArray(fields) && fields.length > 0) {
    fields.forEach((f: unknown, i: number) => {
      if (f && typeof f === 'object') {
        const o = f as Record<string, unknown>;
        const label = String(o.label ?? o.libelle ?? o.name ?? o.nom ?? o.key ?? `Champ ${i + 1}`).trim();
        const val = o.value ?? o.valeur ?? o.reponse ?? '';
        const value = typeof val === 'object' && val !== null ? JSON.stringify(val) : String(val ?? '').trim();
        if (label) entries.push({ label, value: value || '—' });
      }
    });
    if (entries.length > 0) return entries;
  }

  const raw = Object.entries(formData).filter(([k]) => !skipKeys.test(k));
  const hasNumericKeys = raw.length > 0 && raw.every(([k]) => /^\d+$/.test(k));

  if (hasNumericKeys) {
    raw.sort(([a], [b]) => parseInt(a, 10) - parseInt(b, 10));
    raw.forEach(([key, value]) => {
      const display = formatFormValue(value);
      entries.push({ label: `Élément ${parseInt(key, 10) + 1}`, value: display });
    });
  } else {
    raw.forEach(([key, value]) => {
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      entries.push({ label, value: formatFormValue(value) });
    });
  }
  return entries;
}

function formatFormValue(value: unknown): string {
  if (value == null || value === '') return '—';
  if (Array.isArray(value)) {
    return value.map((v) => (typeof v === 'object' && v !== null && !Array.isArray(v)
      ? String((v as Record<string, unknown>).name ?? (v as Record<string, unknown>).nom ?? (v as Record<string, unknown>).label ?? JSON.stringify(v))
      : String(v))).join(', ');
  }
  if (typeof value === 'object') {
    const o = value as Record<string, unknown>;
    return String(o.name ?? o.nom ?? o.label ?? o.libelle ?? o.valeur ?? o.value ?? JSON.stringify(value));
  }
  return String(value);
}

export default function GestionRapportsCyberModal({ isOpen, onClose }: GestionRapportsCyberModalProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<'client' | 'formulaires' | 'formulaire' | 'generating' | 'done'>('client');
  const [servo2ClientNames, setServo2ClientNames] = useState<string[]>([]);
  const [clientName, setClientName] = useState('');
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [formulaires, setFormulaires] = useState<IncidentFormListItem[]>([]);
  const [selectedForm, setSelectedForm] = useState<IncidentFormListItem | null>(null);
  const [formData, setFormData] = useState<IncidentFormData | null>(null);
  const [prismeClients, setPrismeClients] = useState<ClientItem[]>([]);
  const [selectedPrismeClient, setSelectedPrismeClient] = useState<ClientItem | null>(null);
  const [prismeClientSearch, setPrismeClientSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [, setCreatedDocumentId] = useState<number | null>(null);
  const prevIsOpen = useRef(false);

  useEffect(() => {
    if (isOpen && !prevIsOpen.current) {
      setStep('client');
      setServo2ClientNames([]);
      setClientSearchQuery('');
      setError(null);
      setClientName('');
      setFormulaires([]);
      setSelectedForm(null);
      setFormData(null);
      setSelectedPrismeClient(null);
      setPrismeClientSearch('');
    }
    prevIsOpen.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || step !== 'client') return;
    setError(null);
    setLoading(true);
    getServo2ClientNames()
      .then((names) => {
        setServo2ClientNames(Array.isArray(names) ? names : []);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Erreur chargement des clients Servo2');
        setServo2ClientNames([]);
      })
      .finally(() => setLoading(false));
  }, [isOpen, step]);

  useEffect(() => {
    if (!isOpen || step !== 'formulaire') return;
    getAllClients()
      .then((list) => setPrismeClients((list || []).filter((c) => c.actif !== false)))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Erreur chargement des clients PRIsME'));
  }, [isOpen, step]);

  const handleSelectServo2Client = (name: string) => {
    setClientName(name);
    setError(null);
    setFormulaires([]);
    setSelectedForm(null);
    setFormData(null);
    setStep('formulaires');
    fetchFormulairesByClientName(name);
  };

  /** Charge les formulaires Servo2 pour le client (API déjà filtrée par client_name). Tri du plus récent au plus vieux. */
  const fetchFormulairesByClientName = (name: string) => {
    if (!name?.trim()) return;
    setLoading(true);
    setError(null);
    getFormulairesByClientName(name.trim())
      .then((list) => {
        const filtered = filterFormsByClientName(list, name.trim());
        const listToShow = filtered.length > 0 ? filtered : list;
        const sorted = sortFormulairesByDateDesc(listToShow);
        setFormulaires(sorted);
        setLoading(false);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : 'Erreur chargement des formulaires');
        setLoading(false);
      });
  };

  /**
   * Charge le détail du formulaire pour récupérer toutes les valeurs remplies dans Servo2.
   * Fallback: si l'API détail échoue, on utilise l'item de liste (qui contient déjà plusieurs champs utiles).
   */
  const handleSelectForm = async (form: IncidentFormListItem) => {
    setSelectedForm(form);
    setError(null);
    setStep('formulaire');
    setLoading(true);

    const id = getFormId(form);
    try {
      const detail = await getFormulaireById(id, clientName);
      const merged = {
        ...(form as unknown as IncidentFormData),
        ...(detail as unknown as IncidentFormData),
      };
      setFormData(merged);
    } catch (e: unknown) {
      // Fallback: on conserve les infos minimales disponibles
      setFormData(form as unknown as IncidentFormData);
      setError(
        e instanceof Error
          ? `Impossible de charger le détail du formulaire Servo2 (fallback liste): ${e.message}`
          : "Impossible de charger le détail du formulaire Servo2 (fallback liste)."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!selectedPrismeClient || !formData) {
      setError('Veuillez sélectionner un client PRIsME pour générer le rapport.');
      return;
    }
    setError(null);
    setStep('generating');
    setLoading(true);
    try {
      const formLabel = selectedForm ? getFormLabel(selectedForm) : undefined;
      const response = await generateRapportIncidentFromServo2({
        clientId: selectedPrismeClient.id!,
        prismeClientName: selectedPrismeClient.nom,
        servo2ClientName: clientName,
        formId: selectedForm ? getFormId(selectedForm) : undefined,
        formData,
        qualityLevel: 3,
        formLabel,
      });
      setCreatedDocumentId(response.documentId ?? null);
      setStep('done');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur lors de la création du document');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (step === 'formulaire') {
      setSelectedForm(null);
      setFormData(null);
      setSelectedPrismeClient(null);
      setPrismeClientSearch('');
      setStep('formulaires');
    } else if (step === 'formulaires') {
      setClientName('');
      setClientSearchQuery('');
      setFormulaires([]);
      setStep('client');
    }
    setError(null);
  };

  const handleClose = () => {
    setStep('client');
    setClientName('');
    setClientSearchQuery('');
    setFormulaires([]);
    setSelectedForm(null);
    setFormData(null);
    setSelectedPrismeClient(null);
    setPrismeClientSearch('');
    setCreatedDocumentId(null);
    setError(null);
    onClose();
  };

  const filteredServo2Clients = clientSearchQuery.trim()
    ? servo2ClientNames.filter((name) =>
        name.toLowerCase().includes(clientSearchQuery.trim().toLowerCase())
      )
    : servo2ClientNames;

  const filteredPrismeClients = prismeClientSearch.trim()
    ? prismeClients.filter((c) =>
        c.nom.toLowerCase().includes(prismeClientSearch.trim().toLowerCase())
      )
    : prismeClients;

  const goToGestionDocumentaire = () => {
    handleClose();
    navigate('/gestion-documentaire');
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-gradient-to-br from-slate-900 via-red-900/30 to-slate-900 rounded-2xl max-w-2xl w-full my-8 border border-red-500/30 shadow-2xl"
        >
          <div className="p-6 border-b border-red-500/30 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-500/20 rounded-xl">
                <Shield className="w-8 h-8 text-red-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Gestion des rapports d'incident</h2>
                <p className="text-gray-400 text-sm">Clients Servo2 → Formulaire → Lier à un client PRIsME → Document</p>
              </div>
            </div>
            <button onClick={handleClose} className="p-2 hover:bg-white/10 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="p-6 min-h-[320px]">
            {error && (
              <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm">
                {error}
              </div>
            )}

            <AnimatePresence mode="wait">
              {step === 'client' && (
                <motion.div
                  key="client"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="space-y-4"
                >
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-red-400" />
                    Choisir un client Servo2
                  </h3>
                  <p className="text-gray-400 text-sm">Clients issus des formulaires incident Servo2 (API).</p>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      value={clientSearchQuery}
                      onChange={(e) => setClientSearchQuery(e.target.value)}
                      placeholder="Rechercher un client par nom…"
                      className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-white placeholder-gray-500 focus:border-red-500/50 focus:ring-1 focus:ring-red-500/30 outline-none"
                    />
                  </div>
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 text-red-400 animate-spin" />
                    </div>
                  ) : (
                    <ul className="space-y-2 max-h-64 overflow-y-auto">
                      {filteredServo2Clients.length === 0 ? (
                        <li className="text-gray-400 py-2">Aucun client Servo2 ne correspond à la recherche.</li>
                      ) : (
                        filteredServo2Clients.map((name) => (
                          <li key={name}>
                            <button
                              type="button"
                              onClick={() => handleSelectServo2Client(name)}
                              className="w-full text-left px-4 py-3 rounded-xl bg-slate-800/60 hover:bg-red-500/20 border border-slate-600/50 hover:border-red-500/40 flex items-center justify-between text-white transition-colors"
                            >
                              <span>{name}</span>
                              <ChevronRight className="w-5 h-5 text-gray-400" />
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                  )}
                </motion.div>
              )}

              {step === 'formulaires' && (
                <motion.div
                  key="formulaires"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="space-y-4"
                >
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <button type="button" onClick={handleBack} className="hover:text-white underline">
                      Retour
                    </button>
                    <span>/</span>
                    <span className="text-white">{clientName}</span>
                  </div>
                  <p className="text-gray-400 text-sm">
                    Association par <strong className="text-white">nom du client</strong> avec Servo2. Ajustez le nom ci-dessous si aucun formulaire n’apparaît.
                  </p>
                  <div className="flex flex-wrap items-center gap-2">
                    <label className="text-gray-400 text-sm whitespace-nowrap">Nom du client (Servo2) :</label>
                    <input
                      type="text"
                      value={clientName}
                      onChange={(e) => setClientName(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && fetchFormulairesByClientName(clientName)}
                      placeholder="Nom tel qu’enregistré dans Servo2"
                      className="flex-1 min-w-[180px] px-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-white placeholder-gray-500 focus:border-red-500/50 focus:ring-1 focus:ring-red-500/30 outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => fetchFormulairesByClientName(clientName)}
                      disabled={loading || !clientName.trim()}
                      className="px-4 py-2 rounded-lg bg-red-500/20 border border-red-500/50 text-red-200 hover:bg-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      {loading ? 'Recherche…' : 'Rechercher'}
                    </button>
                  </div>
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-red-400" />
                    Formulaires du client (du plus récent au plus ancien)
                  </h3>
                  {loading ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-8 h-8 text-red-400 animate-spin" />
                    </div>
                  ) : formulaires.length === 0 ? (
                    <p className="text-gray-400 py-4">Aucun formulaire trouvé pour ce client.</p>
                  ) : (
                    <ul className="space-y-2 max-h-64 overflow-y-auto">
                      {formulaires.map((f, index) => (
                        <li key={String(getFormId(f, index))}>
                          <button
                            type="button"
                            onClick={() => handleSelectForm(f)}
                            className="w-full text-left px-4 py-3 rounded-xl bg-slate-800/60 hover:bg-red-500/20 border border-slate-600/50 hover:border-red-500/40 flex items-center justify-between text-white transition-colors gap-2"
                          >
                            <span className="flex flex-col items-start min-w-0">
                              <span className="font-medium truncate">{getFormLabel(f)}</span>
                              {getFormDate(f) && (
                                <span className="text-gray-400 text-sm">Début : {getFormDate(f)}</span>
                              )}
                            </span>
                            <ChevronRight className="w-5 h-5 text-gray-400 shrink-0" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </motion.div>
              )}

              {step === 'formulaire' && formData && (
                <motion.div
                  key="formulaire"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 10 }}
                  className="space-y-4"
                >
                  <div className="flex items-center gap-2 text-gray-400 text-sm">
                    <button type="button" onClick={handleBack} className="hover:text-white underline">
                      Retour
                    </button>
                    <span>/</span>
                    <span className="text-white">{getFormLabel(selectedForm!)}</span>
                  </div>
                  <h4 className="text-sm font-medium text-white">Informations du formulaire</h4>
                  <p className="text-gray-400 text-xs">Client Servo2 : <span className="text-white">{clientName}</span></p>
                  <div className="rounded-lg border border-slate-600 bg-slate-800/60 p-4 max-h-56 overflow-y-auto text-sm space-y-2">
                    {getFormDisplayEntries(formData).length === 0 ? (
                      <p className="text-gray-400">Aucune donnée de formulaire à afficher.</p>
                    ) : (
                      getFormDisplayEntries(formData).map(({ label, value }, idx) => (
                        <div key={`${label}-${idx}`} className="flex gap-2">
                          <span className="text-gray-400 shrink-0">{label} :</span>
                          <span className="text-gray-200 break-words">{value}</span>
                        </div>
                      ))
                    )}
                  </div>
                  <p className="text-gray-400 text-sm">
                    Les informations ci-dessus serviront à générer automatiquement un document{' '}
                    <span className="text-white">« Rapport d&apos;incident TI »</span> pour le client PRIsME choisi, et à
                    l&apos;enregistrer dans la Gestion documentaire.
                  </p>
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-white">Associer à un client PRIsME</h4>
                    <p className="text-gray-400 text-sm">
                      Choisissez le client PRIsME pour lequel créer la prise de besoin « Rapport d&apos;incident TI v1 ».
                    </p>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <input
                        type="text"
                        value={prismeClientSearch}
                        onChange={(e) => setPrismeClientSearch(e.target.value)}
                        placeholder="Rechercher un client PRIsME…"
                        className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-800 border border-slate-600 text-white placeholder-gray-500 focus:border-red-500/50 focus:ring-1 focus:ring-red-500/30 outline-none"
                      />
                    </div>
                    <ul className="max-h-40 overflow-y-auto space-y-1 border border-slate-600 rounded-lg p-2 bg-slate-800/60">
                      {filteredPrismeClients.length === 0 ? (
                        <li className="text-gray-400 text-sm py-1">Aucun client PRIsME trouvé.</li>
                      ) : (
                        filteredPrismeClients.map((c) => (
                          <li key={c.id}>
                            <button
                              type="button"
                              onClick={() => setSelectedPrismeClient(c)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                selectedPrismeClient?.id === c.id
                                  ? 'bg-red-500/30 text-white border border-red-500/50'
                                  : 'text-gray-300 hover:bg-slate-700'
                              }`}
                            >
                              {c.nom}
                            </button>
                          </li>
                        ))
                      )}
                    </ul>
                    {selectedPrismeClient && (
                      <p className="text-gray-400 text-sm">Client sélectionné : <span className="text-white">{selectedPrismeClient.nom}</span></p>
                    )}
                    <button
                      type="button"
                      onClick={handleGenerateReport}
                      disabled={!selectedPrismeClient}
                      className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-500 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-xl transition-colors"
                    >
                      Générer le document « Rapport d&apos;incident TI »
                    </button>
                  </div>
                </motion.div>
              )}

              {step === 'generating' && (
                <motion.div
                  key="generating"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center py-12"
                >
                  <Loader2 className="w-12 h-12 text-red-400 animate-spin mb-4" />
                  <p className="text-white font-medium">Création du rapport et enregistrement…</p>
                </motion.div>
              )}

              {step === 'done' && (
                <motion.div
                  key="done"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="flex flex-col items-center justify-center py-8 text-center"
                >
                  <CheckCircle className="w-16 h-16 text-green-400 mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Rapport généré</h3>
                  <p className="text-gray-400 mb-6">
                    Le document « Rapport d&apos;incident TI » est disponible dans Gestion documentaire → Livrables créés
                    → Documents.
                  </p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={goToGestionDocumentaire}
                      className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white font-medium rounded-xl transition-colors"
                    >
                      Ouvrir la Gestion documentaire
                    </button>
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-4 py-2 bg-slate-600 hover:bg-slate-500 text-white font-medium rounded-xl transition-colors"
                    >
                      Fermer
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
